/**
 * Module Intelligence Dashboard - PREMIUM MODERN DESIGN
 * Comprehensive module health monitoring with detailed insights
 * Enhanced with data volume metrics, rankings, and expandable insights
 */

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { protectedResources } from '../config/msalConfig';
import {
    Row,
    Col,
    Typography,
    Progress,
    Button,
    Dropdown,
    Spin,
    Collapse,
    Table,
    Tag,
    Tooltip,
    Statistic
} from 'antd';
import {
    ThunderboltFilled,
    EyeOutlined,
    ReloadOutlined,
    TrophyOutlined,
    RiseOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    MinusOutlined,
    FileTextOutlined,
    DatabaseOutlined,
    BulbOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    BarChartOutlined,
    LineChartOutlined,
    AppstoreOutlined,
    UnorderedListOutlined,
    DownOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import GaugeChart from 'react-gauge-chart';
import { getModuleIntelligence } from '../services/digestService';
import { useUser } from '../components/UserContext';
import { useNavigate } from 'react-router-dom';
import BriefingGenerator from '../views/ExecutiveBriefing/components/BriefingGenerator';

const { Text } = Typography;
const { Panel } = Collapse;

const ModuleCard = styled.div`
    position: relative;
    height: 100%;
    background: white;
    border-radius: 8px;
    border: 1px solid #f0f0f0;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: ${props => props.accentColor || '#1890ff'};
        transform: scaleX(0);
        transform-origin: left;
        transition: transform 0.3s ease;
    }

    &:hover {
        border-color: ${props => props.accentColor || '#1890ff'};
        box-shadow: 0 4px 12px ${props => props.accentColor ? `${props.accentColor}20` : 'rgba(24, 144, 255, 0.12)'};
        transform: translateY(-1px);

        &::before {
            transform: scaleX(1);
        }
    }
`;

const CompactHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    background: white;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 12px;

    .title {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
        margin: 0;
    }

    .subtitle {
        font-size: 12px;
        color: #8c8c8c;
        margin-left: 8px;
    }
`;

const CompactStatsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    background: #fafafa;
    border-radius: 8px;
    margin-bottom: 16px;
    border: 1px solid #f0f0f0;

    .stats-left {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
    }

    .stats-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-left: auto;
        flex-shrink: 0;
    }
`;

const CompactGauge = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding-right: 16px;
    border-right: 1px solid #e8e8e8;

    .gauge-container {
        position: relative;
        width: 100px;
        height: 60px;
        flex-shrink: 0;

        .score-overlay {
            position: absolute;
            bottom: -2px;
            left: 50%;
            transform: translateX(-50%);
            font-size: 22px;
            font-weight: 700;
            color: ${props => props.scoreColor || '#262626'};
            pointer-events: none;
            background: white;
            padding: 0 6px;
            border-radius: 4px;
            line-height: 1;
        }
    }

    .gauge-info {
        .status {
            font-size: 14px;
            font-weight: 600;
            color: #262626;
            margin-bottom: 2px;
        }

        .subtitle {
            font-size: 11px;
            color: #8c8c8c;
        }
    }
`;

const CompactPill = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    background: ${props => props.background || '#f0f0f0'};
    border: 1px solid ${props => props.borderColor || '#e8e8e8'};
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;

    .count {
        font-size: 15px;
        font-weight: 700;
        color: ${props => props.color || '#262626'};
    }

    .label {
        color: #595959;
        font-size: 11px;
    }

    .icon {
        font-size: 13px;
        color: ${props => props.color || '#262626'};
    }
`;

const RankingTable = styled(Table)`
    .ant-table-thead > tr > th {
        background: #fafafa;
        color: #595959;
        font-weight: 600;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        border-bottom: 1px solid #e8e8e8;
    }
`;

const ScopeBanner = styled.div`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 10px 16px;
    border-radius: 8px;
    margin-bottom: 14px;
    color: white;

    .scope-title {
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 6px;
        opacity: 0.9;
    }

    .scope-stats {
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
    }

    .scope-stat {
        .value {
            font-size: 22px;
            font-weight: 700;
            line-height: 1;
            margin-bottom: 4px;
        }

        .label {
            font-size: 12px;
            opacity: 0.85;
        }
    }
`;

const ViewToggle = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 0;
    padding: 3px;
    background: #eef0f3;
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px #e0e5ea;
    height: 32px;
`;

const ViewToggleIndicator = styled.div`
    position: absolute;
    top: 3px;
    left: 3px;
    width: 30px;
    height: 26px;
    border-radius: 999px;
    background: #f88109;
    transition: transform 0.2s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    pointer-events: none;
    z-index: 0;
`;

const ViewToggleButton = styled.button`
    width: 30px;
    height: 26px;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: ${props => (props.$active ? '#ffffff' : '#6b7280')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: color 0.2s ease;
    padding: 0;
    line-height: 1;
    z-index: 1;

    svg {
        width: 17px;
        height: 17px;
        display: block;
    }
`;

export default function ModuleIntelligenceDashboard({ onModuleClick }) {
    const { instance, accounts } = useMsal();
    const { userDetails } = useUser();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [intelligenceData, setIntelligenceData] = useState(null);
    const [expandedModules, setExpandedModules] = useState([]);
    const [moduleView, setModuleView] = useState('table');
    const [showBriefingGenerator, setShowBriefingGenerator] = useState(false);

    useEffect(() => {
        if (userDetails?.CustomerID) {
            loadModuleIntelligence();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDetails?.CustomerID]);

    const loadModuleIntelligence = async () => {
        try {
            setLoading(true);
            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            const result = await getModuleIntelligence(userDetails.CustomerID, response.accessToken);

            if (result.success && result.data) {
                setIntelligenceData(result.data);
            }
        } catch (error) {
            console.error('Error loading module intelligence:', error);
        } finally {
            setLoading(false);
        }
    };

    const getHealthColor = (score) => {
        if (score >= 80) return '#52c41a';
        if (score >= 60) return '#1890ff';
        if (score >= 40) return '#faad14';
        return '#ff4d4f';
    };

    const getHealthGradient = (score) => {
        if (score >= 80) return 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)';
        if (score >= 60) return 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)';
        if (score >= 40) return 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)';
        return 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)';
    };

    const getHealthStatus = (score) => {
        if (score >= 80) return 'Excellent';
        if (score >= 60) return 'Good';
        if (score >= 40) return 'Fair';
        return 'Needs Attention';
    };

    const getTrendIcon = (direction) => {
        if (direction === 'IMPROVING') return <ArrowUpOutlined style={{ color: '#52c41a' }} />;
        if (direction === 'DECLINING') return <ArrowDownOutlined style={{ color: '#ff4d4f' }} />;
        return <MinusOutlined style={{ color: '#8c8c8c' }} />;
    };

    const getTrendColor = (direction) => {
        if (direction === 'IMPROVING') return 'success';
        if (direction === 'DECLINING') return 'error';
        return 'default';
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!intelligenceData) {
        return null;
    }

    const { modules, overallStats, topPerformers, needsAttention } = intelligenceData;
    const overallScore = overallStats.averageHealthScore || 0;
    const healthyModules = modules.filter(m => m.healthScore >= 80);
    const improvingModules = modules.filter(m => m.healthScore >= 60 && m.healthScore < 80);
    const attentionModules = modules.filter(m => m.healthScore < 60);

    const briefingMenuItems = [
        { key: 'generate', label: 'Generate New Briefing' },
        { key: 'history', label: 'View Briefing History' }
    ];

    const handleBriefingMenuClick = ({ key }) => {
        if (key === 'generate') {
            setShowBriefingGenerator(true);
            return;
        }
        navigate('/executive-briefing');
    };

    // Ranking table columns
    const rankingColumns = [
        {
            title: 'Rank',
            key: 'rank',
            width: 60,
            render: (_, __, index) => {
                if (index === 0) return <TrophyOutlined style={{ color: '#FFD700', fontSize: '18px' }} />;
                if (index === 1) return <TrophyOutlined style={{ color: '#C0C0C0', fontSize: '18px' }} />;
                if (index === 2) return <TrophyOutlined style={{ color: '#CD7F32', fontSize: '18px' }} />;
                return index + 1;
            }
        },
        {
            title: 'Module',
            dataIndex: 'moduleName',
            key: 'moduleName',
            width: 200,
            ellipsis: true,
            render: (text, record) => (
                <div style={{ maxWidth: 180 }}>
                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {text}
                    </div>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                        {record.moduleShortName}
                    </Text>
                </div>
            )
        },
        {
            title: 'Health Score',
            dataIndex: 'healthScore',
            key: 'healthScore',
            width: 180,
            sorter: (a, b) => b.healthScore - a.healthScore,
            render: (score) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Progress
                        percent={score}
                        strokeColor={getHealthColor(score)}
                        strokeWidth={8}
                        showInfo={false}
                        style={{ flex: 1, minWidth: '60px' }}
                    />
                    <span style={{ fontWeight: 600, color: getHealthColor(score), minWidth: '35px' }}>
                        {score}/100
                    </span>
                </div>
            )
        },
        {
            title: 'Trend',
            dataIndex: 'trendDirection',
            key: 'trendDirection',
            width: 100,
            filters: [
                { text: 'Improving', value: 'IMPROVING' },
                { text: 'Stable', value: 'STABLE' },
                { text: 'Declining', value: 'DECLINING' }
            ],
            onFilter: (value, record) => record.trendDirection === value,
            render: (trend, record) => (
                <Tooltip title={`7-day: ${record.trends.healthScore7Day} | 30-day: ${record.trends.healthScore30Day}`}>
                    <Tag icon={getTrendIcon(trend)} color={getTrendColor(trend)}>
                        {trend}
                    </Tag>
                </Tooltip>
            )
        },
        {
            title: 'Data Volume',
            key: 'dataVolume',
            width: 220,
            render: (_, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div>
                        <FileTextOutlined style={{ marginRight: '4px', color: '#1890ff' }} />
                        {record.dataVolume.totalReports} reports
                        <span style={{ color: '#52c41a', marginLeft: '4px' }}>
                            ({record.dataVolume.analyzedReports} analyzed)
                        </span>
                    </div>
                    <div>
                        <DatabaseOutlined style={{ marginRight: '4px', color: '#52c41a' }} />
                        {record.dataVolume.totalPages} pages
                        <span style={{ color: '#52c41a', marginLeft: '4px' }}>
                            ({record.dataVolume.analyzedPages} analyzed)
                        </span>
                    </div>
                </div>
            ),
            sorter: (a, b) => b.dataVolume.totalPages - a.dataVolume.totalPages
        },
        {
            title: 'Status',
            dataIndex: 'healthStatus',
            key: 'healthStatus',
            width: 120,
            filters: [
                { text: 'Excellent', value: 'EXCELLENT' },
                { text: 'Good', value: 'GOOD' },
                { text: 'Fair', value: 'FAIR' },
                { text: 'Needs Attention', value: 'NEEDS_ATTENTION' }
            ],
            onFilter: (value, record) => record.healthStatus === value,
            render: (status) => {
                const colorMap = {
                    EXCELLENT: 'success',
                    GOOD: 'processing',
                    FAIR: 'warning',
                    NEEDS_ATTENTION: 'error'
                };
                return <Tag color={colorMap[status]}>{status.replace('_', ' ')}</Tag>;
            }
        }
    ];

    return (
        <div style={{ padding: '0' }}>
            {/* Analysis Scope Banner */}
            <ScopeBanner>
                <div className="scope-title">
                    <BarChartOutlined style={{ marginRight: '8px' }} />
                    Total Analysis Scope
                </div>
                <div className="scope-stats">
                    <div className="scope-stat">
                        <div className="value">{overallStats.totalModules}</div>
                        <div className="label">Modules Analyzed</div>
                    </div>
                    <div className="scope-stat">
                        <div className="value">{overallStats.totalReports}</div>
                        <div className="label">Reports Processed</div>
                    </div>
                    <div className="scope-stat">
                        <div className="value">{overallStats.totalPages}</div>
                        <div className="label">Pages Summarized</div>
                    </div>
                </div>
            </ScopeBanner>

            {/* Compact Stats Row */}
            <CompactStatsRow>
                <div className="stats-left">
                    {/* Compact Gauge */}
                    <CompactGauge scoreColor={getHealthColor(overallScore)}>
                        <div className="gauge-container">
                            <GaugeChart
                                id="health-gauge"
                                nrOfLevels={4}
                                colors={['#ff4d4f', '#faad14', '#1890ff', '#52c41a']}
                                arcWidth={0.3}
                                percent={overallScore / 100}
                                textColor="transparent"
                                needleColor="#000000"
                                needleBaseColor="#000000"
                                hideText={true}
                                animDelay={0}
                                arcPadding={0.02}
                            />
                            <div className="score-overlay">{overallScore}</div>
                        </div>
                        <div className="gauge-info">
                            <div className="status">{getHealthStatus(overallScore)}</div>
                            <div className="subtitle">Overall Health</div>
                        </div>
                    </CompactGauge>

                    {/* Compact Pills */}
                    <CompactPill
                        background="#f0fdf4"
                        borderColor="#bbf7d0"
                        color="#15803d"
                    >
                        <TrophyOutlined className="icon" />
                        <span className="count">{healthyModules.length}</span>
                        <span className="label">Excellent</span>
                    </CompactPill>

                    <CompactPill
                        background="#eff6ff"
                        borderColor="#bfdbfe"
                        color="#1e40af"
                    >
                        <RiseOutlined className="icon" />
                        <span className="count">{improvingModules.length}</span>
                        <span className="label">Good</span>
                    </CompactPill>

                    <CompactPill
                        background="#fef9c3"
                        borderColor="#fde047"
                        color="#a16207"
                    >
                        <WarningOutlined className="icon" />
                        <span className="count">{attentionModules.length}</span>
                        <span className="label">Needs Focus</span>
                    </CompactPill>
                </div>
                <div className="stats-actions">
                    <Dropdown menu={{ items: briefingMenuItems, onClick: handleBriefingMenuClick }}>
                        <Button
                            type="primary"
                            size="small"
                            style={{
                                background: '#F16622',
                                borderColor: '#F16622',
                                height: 30,
                                borderRadius: 8,
                                fontWeight: 600,
                                letterSpacing: '0.2px',
                                padding: '0 12px'
                            }}
                        >
                            Executive Briefing <DownOutlined />
                        </Button>
                    </Dropdown>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={loadModuleIntelligence}
                        size="small"
                        type="text"
                    >
                        Refresh
                    </Button>
                </div>
            </CompactStatsRow>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                    marginTop: '25px',
                    paddingRight: '12px',
                    gap: '12px'
                }}
            >
                <h4
                    style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        margin: 0,
                        color: '#262626',
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        minWidth: 0
                    }}
                >
                    {moduleView === 'table' ? (
                        <TrophyOutlined style={{ marginRight: '8px', color: '#F16622' }} />
                    ) : (
                        <AppstoreOutlined style={{ marginRight: '8px', color: '#F16622' }} />
                    )}
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        Module Performance Insights
                    </span>
                </h4>
                <ViewToggle>
                <ViewToggleIndicator
                    style={{ transform: moduleView === 'cards' ? 'translateX(30px)' : 'translateX(0px)' }}
                />
                    <ViewToggleButton
                        type="button"
                        $active={moduleView === 'table'}
                        onClick={() => setModuleView('table')}
                        aria-label="Table view"
                        aria-pressed={moduleView === 'table'}
                    >
                        <UnorderedListOutlined />
                    </ViewToggleButton>
                    <ViewToggleButton
                        type="button"
                        $active={moduleView === 'cards'}
                        onClick={() => setModuleView('cards')}
                        aria-label="Card view"
                        aria-pressed={moduleView === 'cards'}
                    >
                        <AppstoreOutlined />
                    </ViewToggleButton>
                </ViewToggle>
            </div>

            {moduleView === 'table' && (
                <div style={{ marginBottom: '24px' }}>
                    <RankingTable
                        columns={rankingColumns}
                        dataSource={modules}
                        rowKey="moduleId"
                        size="small"
                        tableLayout="fixed"
                        pagination={{ pageSize: 10, showSizeChanger: true }}
                        expandable={{
                            expandedRowRender: (record) => {
                                // Parse JSON summary if it's a string
                                let parsedSummary = null;
                                try {
                                    if (typeof record.summary === 'string') {
                                        parsedSummary = JSON.parse(record.summary);
                                    } else {
                                        parsedSummary = record.summary;
                                    }
                                } catch (e) {
                                    // If parsing fails, keep as plain text
                                    parsedSummary = null;
                                }

                                return (
                                <div style={{ padding: '12px', background: '#fafafa' }}>
                                    <Row gutter={[16, 16]}>
                                    {/* Executive Summary */}
                                    {parsedSummary?.executiveSummary && (
                                        <Col span={24}>
                                            <div style={{ marginBottom: '16px', padding: '12px', background: 'white', borderRadius: '6px', borderLeft: '3px solid #1890ff' }}>
                                                <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                                                    <BulbOutlined style={{ marginRight: '6px' }} />
                                                    Executive Summary
                                                </Text>
                                                <div style={{ marginTop: '8px', fontSize: '13px', color: '#595959', lineHeight: '1.6' }}>
                                                    {parsedSummary.executiveSummary}
                                                </div>
                                            </div>
                                        </Col>
                                    )}

                                    {/* Key Findings */}
                                    {parsedSummary?.keyFindings && parsedSummary.keyFindings.length > 0 && (
                                        <Col span={24}>
                                            <div style={{ marginBottom: '16px', padding: '12px', background: 'white', borderRadius: '6px', borderLeft: '3px solid #52c41a' }}>
                                                <Text strong style={{ fontSize: '14px', color: '#52c41a' }}>
                                                    <CheckCircleOutlined style={{ marginRight: '6px' }} />
                                                    Key Findings
                                                </Text>
                                                <ul style={{ marginTop: '8px', fontSize: '13px', paddingLeft: '20px', marginBottom: 0 }}>
                                                    {parsedSummary.keyFindings.slice(0, 5).map((finding, idx) => (
                                                        <li key={idx} style={{ marginBottom: '6px', color: '#595959', lineHeight: '1.5' }}>
                                                            {typeof finding === 'string' ? finding : finding.description || finding.finding || JSON.stringify(finding)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </Col>
                                    )}

                                    {/* Considerations */}
                                    {parsedSummary?.recommendations && parsedSummary.recommendations.length > 0 && (
                                        <Col span={24}>
                                            <div style={{ marginBottom: '16px', padding: '12px', background: 'white', borderRadius: '6px', borderLeft: '3px solid #faad14' }}>
                                                <Text strong style={{ fontSize: '14px', color: '#faad14' }}>
                                                    <BulbOutlined style={{ marginRight: '6px' }} />
                                                    Considerations
                                                </Text>
                                                <ul style={{ marginTop: '8px', fontSize: '13px', paddingLeft: '20px', marginBottom: 0 }}>
                                                    {parsedSummary.recommendations.slice(0, 5).map((rec, idx) => (
                                                        <li key={idx} style={{ marginBottom: '6px', color: '#595959', lineHeight: '1.5' }}>
                                                            {typeof rec === 'string' ? rec : rec.action || rec.recommendation || JSON.stringify(rec)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </Col>
                                    )}

                                    {/* Trends Analysis */}
                                    {parsedSummary?.trendsAnalysis && (
                                        <Col span={24}>
                                            <div style={{ marginBottom: '16px', padding: '12px', background: 'white', borderRadius: '6px', borderLeft: '3px solid #722ed1' }}>
                                                <Text strong style={{ fontSize: '14px', color: '#722ed1' }}>
                                                    <LineChartOutlined style={{ marginRight: '6px' }} />
                                                    Trends Analysis
                                                </Text>
                                                <div style={{ marginTop: '8px', fontSize: '13px', color: '#595959', lineHeight: '1.6' }}>
                                                    {typeof parsedSummary.trendsAnalysis === 'string'
                                                        ? parsedSummary.trendsAnalysis
                                                        : JSON.stringify(parsedSummary.trendsAnalysis)}
                                                </div>
                                            </div>
                                        </Col>
                                    )}

                                    {/* Fallback to plain text summary if no JSON */}
                                    {!parsedSummary?.executiveSummary && record.summary && (
                                        <Col span={24}>
                                            <div style={{ marginBottom: '12px' }}>
                                                <Text strong style={{ fontSize: '13px' }}>Summary:</Text>
                                                <div style={{ marginTop: '8px', fontSize: '12px', color: '#595959', lineHeight: '1.6' }}>
                                                    {typeof record.summary === 'string' && record.summary.length > 500
                                                        ? record.summary.substring(0, 500) + '...'
                                                        : record.summary}
                                                </div>
                                            </div>
                                        </Col>
                                    )}

                                    {/* Insights from record (fallback if no parsed summary) */}
                                    {!parsedSummary?.keyFindings && record.insights && record.insights.length > 0 && (
                                        <Col span={12}>
                                            <div>
                                                <Text strong style={{ fontSize: '13px' }}>
                                                    <BulbOutlined style={{ marginRight: '6px', color: '#faad14' }} />
                                                    Additional Insights:
                                                </Text>
                                                <ul style={{ marginTop: '8px', fontSize: '12px', paddingLeft: '20px' }}>
                                                    {record.insights.map((insight, idx) => (
                                                        <li key={idx} style={{ marginBottom: '6px', color: '#595959' }}>
                                                            {typeof insight === 'string' ? insight : insight.description || JSON.stringify(insight)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </Col>
                                    )}

                                    {/* Action Items (fallback if no parsed recommendations) */}
                                    {!parsedSummary?.recommendations && record.actionItems && record.actionItems.length > 0 && (
                                        <Col span={12}>
                                            <div>
                                                <Text strong style={{ fontSize: '13px' }}>
                                                    <CheckCircleOutlined style={{ marginRight: '6px', color: '#52c41a' }} />
                                                    Additional Actions:
                                                </Text>
                                                <ul style={{ marginTop: '8px', fontSize: '12px', paddingLeft: '20px' }}>
                                                    {record.actionItems.map((item, idx) => (
                                                        <li key={idx} style={{ marginBottom: '6px', color: '#595959' }}>
                                                            {typeof item === 'string' ? item : item.action || JSON.stringify(item)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </Col>
                                    )}

                                    {/* Trends */}
                                    <Col span={24}>
                                        <div style={{ display: 'flex', gap: '24px', marginTop: '12px', padding: '12px', background: 'white', borderRadius: '6px' }}>
                                            <Statistic
                                                title="7-Day Health"
                                                value={record.trends.healthScore7Day}
                                                suffix="/100"
                                                valueStyle={{ fontSize: '16px', color: getHealthColor(record.trends.healthScore7Day) }}
                                            />
                                            <Statistic
                                                title="30-Day Health"
                                                value={record.trends.healthScore30Day}
                                                suffix="/100"
                                                valueStyle={{ fontSize: '16px', color: getHealthColor(record.trends.healthScore30Day) }}
                                            />
                                            <Statistic
                                                title="Change"
                                                value={Math.abs(record.trends.changePercent)}
                                                prefix={record.trends.changePercent > 0 ? <ArrowUpOutlined /> : record.trends.changePercent < 0 ? <ArrowDownOutlined /> : <MinusOutlined />}
                                                suffix="%"
                                                valueStyle={{
                                                    fontSize: '16px',
                                                    color: record.trends.changePercent > 0 ? '#52c41a' : record.trends.changePercent < 0 ? '#ff4d4f' : '#8c8c8c'
                                                }}
                                            />
                                            <Statistic
                                                title="Last Updated"
                                                value={record.lastUpdated ? new Date(record.lastUpdated).toLocaleDateString() : 'N/A'}
                                                valueStyle={{ fontSize: '14px' }}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                </div>
                            );
                            }
                        }}
                    />
                </div>
            )}

            {moduleView === 'cards' && (
                <div style={{ marginBottom: '24px' }}>
                    <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                        {modules.map(module => {
                            const color = getHealthColor(module.healthScore);
                            const status = getHealthStatus(module.healthScore);

                            return (
                                <Col xs={24} sm={12} md={8} lg={6} key={module.moduleId}>
                                    <ModuleCard
                                        accentColor={color}
                                        onClick={() => onModuleClick && onModuleClick(module)}
                                    >
                                        <div style={{ padding: '14px' }}>
                                            {/* Module Header */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                marginBottom: 12
                                            }}>
                                                <div style={{ flex: 1 }}>
                                                    <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 3 }}>
                                                        {module.moduleName}
                                                    </Text>
                                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                                        {status} {getTrendIcon(module.trendDirection)}
                                                    </Text>
                                                </div>
                                                <div style={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: '50%',
                                                    background: getHealthGradient(module.healthScore),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontSize: '17px',
                                                    fontWeight: '700',
                                                    boxShadow: `0 3px 8px ${color}35`
                                                }}>
                                                    {module.healthScore}
                                                </div>
                                            </div>

                                            {/* Progress Bar */}
                                            <Progress
                                                percent={module.healthScore}
                                                strokeColor={{
                                                    '0%': color,
                                                    '100%': color
                                                }}
                                                strokeWidth={6}
                                                showInfo={false}
                                                style={{ marginBottom: 10 }}
                                            />

                                            {/* Data Volume */}
                                            <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: 8 }}>
                                                <DatabaseOutlined style={{ marginRight: '4px' }} />
                                                {module.dataVolume.totalReports} reports ({module.dataVolume.analyzedReports} analyzed),
                                                {' '}{module.dataVolume.totalPages} pages ({module.dataVolume.analyzedPages} analyzed)
                                            </div>

                                            {/* View Button */}
                                            <Button
                                                type="link"
                                                icon={<EyeOutlined />}
                                                style={{
                                                    padding: 0,
                                                    height: 'auto',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    color: color
                                                }}
                                            >
                                                View Details
                                            </Button>
                                        </div>
                                    </ModuleCard>
                                </Col>
                            );
                        })}
                    </Row>
                </div>
            )}

            <BriefingGenerator
                visible={showBriefingGenerator}
                onClose={() => setShowBriefingGenerator(false)}
                onSuccess={() => {}}
            />
        </div>
    );
}
